import React, { useState, useEffect, useRef, useContext } from 'react';
import { ChevronDown } from 'lucide-react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import BaseModal, { ModalScrollContext } from '../../components/common/BaseModal/BaseModal';
import ConfirmationModal from '../../components/common/ConfirmationModal/ConfirmationModal';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import { DropdownIndicator, ClearIndicator } from '../../components/common/SelectComponents';
import { getSelectStyles } from '../../utils/selectStyles';
import SchemaComparisons from './SchemaComparisons';
import ObjectTypeSelector from './ObjectTypeSelector';
import ScheduleJobRun, { SCHEDULE_MODES } from './ScheduleJobRun';
import NotificationsSection, { MAX_NOTIFICATION_EMAILS } from './NotificationsSection';
import AddRepositoryModal from '../repositories/AddRepositoryModal';
import './AddJobModal.css';
import { jobService } from '../../services/apiService';
import { calculatePlacement } from '../../utils/dropdownPositionUtils';
import { validateCron } from '../../utils/cronUtils';

// ─── Static data (mirrors AddJobModal — single source of truth per file) ─────

const tableSubTypes = [
    { key: 'standardTable', label: 'Standard', payloadKey: 'TABLE' },
    { key: 'transientTable', label: 'Transient' },
    { key: 'icebergTable', label: 'Iceberg' },
    { key: 'externalTable', label: 'External' },
    { key: 'dynamicTable', label: 'Dynamic' },
    { key: 'eventTable', label: 'Event' },
    { key: 'hybridTable', label: 'Hybrid' },
];

const viewSubTypes = [
    { key: 'standardView', label: 'Standard', payloadKey: 'VIEW' },
    { key: 'secureView', label: 'Secure' },
    { key: 'materializedView', label: 'Materialized' },
    { key: 'secureMaterializedView', label: 'Secure Materialized' },
    { key: 'recursiveView', label: 'Recursive' },
];

const policySubTypes = [
    { key: 'maskingPolicy', label: 'Masking' },
    { key: 'rowAccessPolicy', label: 'Row Access' },
    { key: 'projectionPolicy', label: 'Projection' },
    { key: 'aggregationPolicy', label: 'Aggregation' },
    { key: 'joinPolicy', label: 'Join' },
    { key: 'privacyPolicy', label: 'Privacy' },
];

const subTypesMap = { table: tableSubTypes, view: viewSubTypes, policy: policySubTypes };

const objectTypeOptions = [
    { key: 'selectAll', label: 'Select All' },
    { key: 'table', label: 'Table', hasSubTypes: true },
    { key: 'view', label: 'View', hasSubTypes: true },
    { key: 'policy', label: 'Policy', hasSubTypes: true },
    { key: 'procedure', label: 'Procedure' },
    { key: 'function', label: 'Function' },
    { key: 'sequence', label: 'Sequence' },
    { key: 'stage', label: 'Stage' },
    { key: 'fileFormat', label: 'File Format' },
    { key: 'pipe', label: 'Pipe' },
    { key: 'stream', label: 'Stream' },
    { key: 'task', label: 'Task' },
    { key: 'externalFunction', label: 'External Function' },
    { key: 'alert', label: 'Alert' },
    { key: 'tag', label: 'Tag' },
];

const allSubTypes = [...tableSubTypes, ...viewSubTypes, ...policySubTypes];

const initialObjectTypes = objectTypeOptions.reduce((acc, option) => {
    acc[option.key] = false;
    return acc;
}, allSubTypes.reduce((acc, sub) => { acc[sub.key] = false; return acc; }, {}));

const initialFormData = { jobName: '', sourceConnection: '', targetConnection: '' };
const initialExpandedTypes = { table: true, view: true, policy: true };
const initialRepositories = { enabled: false, connectionName: '' };
const initialSchedule = {
    enabled: false,
    mode: SCHEDULE_MODES.ONE_TIME,
    date: '',
    time: '',
    cronExpression: '',
    retryAttempts: '',
    offsetTime: '',
};
const initialNotifications = {
    enabled: false,
    failureEmails: [],
    successEmails: [],
    successWithChangeEmails: [],
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Maps schedule field names to their error keys
const SCHEDULE_ERROR_KEYS = {
    date: 'scheduleDate',
    time: 'scheduleTime',
    cronExpression: 'scheduleCron',
    retryAttempts: 'scheduleRetry',
    offsetTime: 'scheduleOffset',
};

// Maps notification category keys to their error keys
const NOTIFICATION_ERROR_KEYS = {
    failureEmails: 'notificationEmailsFailure',
    successEmails: 'notificationEmailsSuccess',
    successWithChangeEmails: 'notificationEmailsSuccessWithChange',
};

const blankSchemaPair = {
    sourceSchema: '',
    targetSchema: '',
    addFilter: false,
    filterExpanded: false,
    includePrefix: [],
    includeSuffix: [],
    excludePrefix: [],
    excludeSuffix: [],
};

// ─── Component ────────────────────────────────────────────────────────────────

const EditJobModal = ({ isOpen, onClose, jobData, onSuccess, setIsLoading }) => {
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [objectTypes, setObjectTypes] = useState(initialObjectTypes);
    const [expandedTypes, setExpandedTypes] = useState(initialExpandedTypes);
    const [sectionExpanded, setSectionExpanded] = useState({ objectTypes: true, schemaComparisons: true });
    const [repositories, setRepositories] = useState(initialRepositories);
    const [schedule, setSchedule] = useState(initialSchedule);
    const [notifications, setNotifications] = useState(initialNotifications);
    const [repositoryConnections, setRepositoryConnections] = useState([]);
    const [isAddRepositoryOpen, setIsAddRepositoryOpen] = useState(false);
    const [isScheduleConfirmOpen, setIsScheduleConfirmOpen] = useState(false);

    const [connections, setConnections] = useState([]);
    const [schemas, setSchemas] = useState({ source: [], target: [] });
    const [loadingConnections, setLoadingConnections] = useState(false);
    const [loadingSchemas, setLoadingSchemas] = useState(false);

    // Whether this job was saved as automated — drives which tab SchemaComparisons shows
    const [isAutomatedJob, setIsAutomatedJob] = useState(false);
    // Pre-populated pairs passed as seed to SchemaComparisons (set once after fetchSchemas)
    const [initialPairs, setInitialPairs] = useState(null);
    // Pre-populated global filter seed for SchemaComparisons (automated jobs only)
    const [initialGlobalFilter, setInitialGlobalFilter] = useState(null);

    // Ref to always hold the latest schema pairs / meta from SchemaComparisons
    const activePairsRef = useRef([]);
    const schemaMetaRef = useRef({ isAutomated: false, globalFilter: null });
    const modalBodyRef = useRef(null);
    const repoSelectRef = useRef(null);

    const [repoPlacement, setRepoPlacement] = useState('bottom');

    const connectionsReady =
        !!formData.sourceConnection &&
        !!formData.targetConnection &&
        formData.sourceConnection !== formData.targetConnection;

    // const [sourceMenuOpen, setSourceMenuOpen] = useState(false);

    // ─── Effects ─────────────────────────────────────────────────────────────

    // useEffect(() => {
    //     if (!modalBodyRef?.current) return; // wait until ref is populated
    //     const el = modalBodyRef.current;
    //     const handler = () => setSourceMenuOpen(false);
    //     el.addEventListener('scroll', handler);
    //     return () => el.removeEventListener('scroll', handler);
    // }, [modalBodyRef, modalBodyRef?.current]); // 👈 both in deps


    useEffect(() => {
        if (isOpen) fetchActiveConnections();
    }, [isOpen]);

    useEffect(() => {
        if (repositories.enabled) {
            fetchRepositoryConnections();
        }
    }, [repositories.enabled]);

    useEffect(() => {
        if (connectionsReady) {
            fetchSchemas();
        } else {
            setSchemas({ source: [], target: [] });
            setIsLoading(false);
        }
    }, [formData.sourceConnection, formData.targetConnection]);

    // ─── API calls ────────────────────────────────────────────────────────────

    const fetchActiveConnections = async () => {
        setIsLoading(true);
        setLoadingConnections(true);
        try {
            const response = await jobService.getActiveConnections();
            setConnections(response || []);

            const sourceExists = response.some(o => o?.id === jobData?.source_connection_id);
            const targetExists = response.some(o => o?.id === jobData?.target_connection_id);

            setFormData({
                jobName: jobData?.name || '',
                sourceConnection: sourceExists ? jobData?.source_connection_id : '',
                targetConnection: targetExists ? jobData?.target_connection_id : '',
            });

            // Determine mode from jobData upfront
            setIsAutomatedJob(!!jobData?.automated_comparison);
        } catch (error) {
            console.error('Error fetching connections:', error);
            toast.error('Failed to load source and target connections');
            setConnections([]);
        } finally {
            setLoadingConnections(false);
        }
    };

    const fetchRepositoryConnections = async () => {
        try {
            const response = await jobService.getActiveGitConnections?.();
            const repoConnections = response || [];
            setRepositoryConnections(repoConnections);

            setRepositories(prev => {
                if (!prev.connectionName) return prev;
                const stillExists = repoConnections.some(r => r.id === prev.connectionName);
                return stillExists ? prev : { ...prev, connectionName: '' };
            });
        } catch (error) {
            console.error('Error fetching repository connections:', error);
            setRepositoryConnections([]);
        }
    };

    const fetchSchemas = async () => {
        setIsLoading(true);
        setLoadingSchemas(true);
        try {
            const payload = {
                source_connection_id: formData.sourceConnection,
                target_connection_id: formData.targetConnection,
            };
            const response = await jobService.getSourceAndTargetSchemas(payload);
            const sourceSchemas = response.source_connection_schemas || [];
            const targetSchemas = response.target_connection_schemas || [];

            setSchemas({ source: sourceSchemas, target: targetSchemas });

            if (jobData) populateFormData(jobData, sourceSchemas, targetSchemas);
        } catch (error) {
            console.error('Error fetching schemas:', error);
            toast.error('Failed to load schemas');
            setSchemas({ source: [], target: [] });
        } finally {
            setIsLoading(false);
            setLoadingSchemas(false);
        }
    };

    // ─── Populate from jobData ────────────────────────────────────────────────

    const populateFormData = (job, sourceSchemas = [], targetSchemas = []) => {
        // ── Schema pairs ──────────────────────────────────────────────────────
        if (job.schema_pairs && job.schema_pairs.length > 0) {
            const pairs = job.schema_pairs.map(pair => {
                const normalizeFilter = (val) => {
                    if (!val) return [];
                    if (Array.isArray(val)) return val.filter(Boolean);
                    return val.split(',').filter(Boolean);
                };

                const includePrefix = normalizeFilter(pair.include_prefix);
                const includeSuffix = normalizeFilter(pair.include_suffix);
                const excludePrefix = normalizeFilter(pair.exclude_prefix);
                const excludeSuffix = normalizeFilter(pair.exclude_suffix);
                const hasFilters = includePrefix.length > 0 || includeSuffix.length > 0
                    || excludePrefix.length > 0 || excludeSuffix.length > 0;

                return {
                    sourceSchema: sourceSchemas.includes(pair.source_schema) ? pair.source_schema : '',
                    targetSchema: targetSchemas.includes(pair.target_schema) ? pair.target_schema : '',
                    addFilter: hasFilters,
                    filterExpanded: hasFilters,
                    includePrefix,
                    includeSuffix,
                    excludePrefix,
                    excludeSuffix,
                };
            });
            setInitialPairs(pairs);
            activePairsRef.current = pairs;
        } else {
            setInitialPairs([{ ...blankSchemaPair }]);
            activePairsRef.current = [{ ...blankSchemaPair }];
        }

        // ── Global filter (automated jobs only) ───────────────────────────────
        if (job.automated_comparison) {
            const normalizeFilter = (val) => {
                if (!val) return [];
                if (Array.isArray(val)) return val.filter(Boolean);
                return val.split(',').filter(Boolean);
            };
            const includePrefix = normalizeFilter(job.global_include_prefix);
            const includeSuffix = normalizeFilter(job.global_include_suffix);
            const excludePrefix = normalizeFilter(job.global_exclude_prefix);
            const excludeSuffix = normalizeFilter(job.global_exclude_suffix);
            const hasGlobal = includePrefix.length > 0 || includeSuffix.length > 0
                || excludePrefix.length > 0 || excludeSuffix.length > 0;

            const gf = {
                enabled: hasGlobal,
                includePrefix,
                includeSuffix,
                excludePrefix,
                excludeSuffix,
            };
            setInitialGlobalFilter(gf);
            schemaMetaRef.current = { isAutomated: true, globalFilter: gf };
        } else {
            setInitialGlobalFilter(null);
            schemaMetaRef.current = { isAutomated: false, globalFilter: null };
        }

        // ── Object types ──────────────────────────────────────────────────────
        if (job.object_types) {
            const types = typeof job.object_types === 'string'
                ? job.object_types.split(',').map(t => t.trim())
                : Array.isArray(job.object_types) ? job.object_types : [];

            const payloadKeyMap = {};
            allSubTypes.forEach(sub => {
                if (sub.payloadKey) payloadKeyMap[sub.payloadKey] = sub.key;
            });

            const newObjectTypes = { ...initialObjectTypes };

            types.forEach(type => {
                if (payloadKeyMap[type]) {
                    newObjectTypes[payloadKeyMap[type]] = true;
                } else {
                    const camelKey = type.toLowerCase().replace(/_([a-z])/g, (_, c) => c.toUpperCase());
                    if (newObjectTypes.hasOwnProperty(camelKey)) {
                        newObjectTypes[camelKey] = true;
                    }
                }
            });

            // Sync parent keys based on subtypes
            Object.keys(subTypesMap).forEach(parentKey => {
                const state = getSubTypeCheckState(parentKey, newObjectTypes);
                newObjectTypes[parentKey] = state !== 'none';
            });

            // Sync selectAll
            const allChecked = objectTypeOptions
                .filter(o => o.key !== 'selectAll')
                .every(opt => opt.hasSubTypes
                    ? getSubTypeCheckState(opt.key, newObjectTypes) === 'all'
                    : newObjectTypes[opt.key]
                );
            newObjectTypes.selectAll = allChecked;

            setObjectTypes(newObjectTypes);
        } else {
            setObjectTypes(initialObjectTypes);
        }

        // ── Repositories ──────────────────────────────────────────────────────
        if (job.github_connection_id) {
            if (job.github_connection) setRepositories({ enabled: true, connectionName: job.github_connection_id });
            else setRepositories({ enabled: true, connectionName: '' });
        } else {
            setRepositories(initialRepositories)
        }

        // ── Schedule Job Run ──────────────────────────────────────────────────
        // Reverses the payload shape built on save (see handleSubmit).
        if (job.schedule?.is_enabled) {
            const s = job.schedule;
            const isCron = s.schedule_type === 'CRON';

            // run_at is stored as a UTC instant. The backend may return it as a
            // naive string (no 'Z'/offset), which new Date() would misread as local —
            // so append 'Z' when no timezone is present, then read local wall-clock.
            let date = '';
            let time = '';
            if (!isCron && s.run_at) {
                const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s.run_at);
                const dt = new Date(hasTz ? s.run_at : `${s.run_at}Z`);
                if (!isNaN(dt)) {
                    const pad = (n) => String(n).padStart(2, '0');
                    date = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
                    time = `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
                }
            }

            setSchedule({
                enabled: true,
                mode: isCron ? SCHEDULE_MODES.CRON : SCHEDULE_MODES.ONE_TIME,
                date,
                time,
                cronExpression: s.cron_expression || '',
                retryAttempts: s.retry_attempts ?? '',
                offsetTime: s.retry_offset_seconds ?? '',
            });
        } else {
            setSchedule(initialSchedule);
        }

        // ── Notifications ─────────────────────────────────────────────────────
        // Independent of the schedule toggle.
        if (job.is_notification_enabled) {
            setNotifications({
                enabled: true,
                failureEmails: Array.isArray(job.notification_emails_failure) ? job.notification_emails_failure : [],
                successEmails: Array.isArray(job.notification_emails_success) ? job.notification_emails_success : [],
                successWithChangeEmails: Array.isArray(job.notification_emails_success_with_changes) ? job.notification_emails_success_with_changes : [],
            });
        } else {
            setNotifications(initialNotifications);
        }
    };

    // ─── Helpers ──────────────────────────────────────────────────────────────

    const getSubTypeCheckState = (parentKey, types) => {
        const subs = subTypesMap[parentKey];
        if (!subs) return types[parentKey] ? 'all' : 'none';
        const anyChecked = subs.some(s => types[s.key]);
        const allChecked = subs.every(s => types[s.key]);
        if (allChecked) return 'all';
        if (anyChecked) return 'partial';
        return 'none';
    };

    const resetDependentData = () => {
        setInitialPairs([{ ...blankSchemaPair }]);
        setInitialGlobalFilter(null);
        setObjectTypes(initialObjectTypes);
        setExpandedTypes(initialExpandedTypes);
        setRepositories(initialRepositories);
        setSchedule(initialSchedule);
        setNotifications(initialNotifications);
        setErrors(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(key => {
                if (key.startsWith('sourceSchema_') || key.startsWith('targetSchema_') || key === 'objectTypes' ||
                    key.startsWith('schedule') || key.startsWith('notification')) {
                    delete next[key];
                }
            });
            return next;
        });
    };

    // ─── Handlers ─────────────────────────────────────────────────────────────

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));

        if (field === 'sourceConnection' || field === 'targetConnection') {
            const sourceConn = field === 'sourceConnection' ? value : formData.sourceConnection;
            const targetConn = field === 'targetConnection' ? value : formData.targetConnection;

            if (sourceConn && targetConn && sourceConn === targetConn) {
                setErrors(prev => ({ ...prev, [field]: 'Source and Target cannot be same' }));
            } else {
                setErrors(prev => {
                    const next = { ...prev };
                    delete next.sourceConnection;
                    delete next.targetConnection;
                    return next;
                });
            }

            resetDependentData();
        }
    };

    // Called by SchemaComparisons whenever pairs, tab, or global filter change
    const handlePairsChange = (pairs, isAutomated, globalFilter) => {
        activePairsRef.current = pairs;
        schemaMetaRef.current = { isAutomated, globalFilter };
    };

    const handleObjectTypeChange = (type) => {
        setObjectTypes(prev => {
            const updated = { ...prev };

            if (type === 'selectAll') {
                const newValue = !prev.selectAll;
                objectTypeOptions.forEach(opt => { updated[opt.key] = newValue; });
                allSubTypes.forEach(sub => { updated[sub.key] = newValue; });
                return updated;
            }

            const parentOption = objectTypeOptions.find(o => o.key === type && o.hasSubTypes);
            if (parentOption) {
                const state = getSubTypeCheckState(type, prev);
                const newValue = state === 'none';
                subTypesMap[type].forEach(sub => { updated[sub.key] = newValue; });
                updated[type] = newValue;
            } else {
                const parentKey = Object.keys(subTypesMap).find(pk => subTypesMap[pk].some(s => s.key === type));
                if (parentKey) {
                    updated[type] = !prev[type];
                    updated[parentKey] = getSubTypeCheckState(parentKey, updated) !== 'none';
                } else {
                    updated[type] = !prev[type];
                }
            }

            const allChecked = objectTypeOptions
                .filter(o => o.key !== 'selectAll')
                .every(opt => opt.hasSubTypes ? getSubTypeCheckState(opt.key, updated) === 'all' : updated[opt.key]);
            updated.selectAll = allChecked;

            return updated;
        });

        if (errors.objectTypes) {
            setErrors(prev => { const next = { ...prev }; delete next.objectTypes; return next; });
        }
    };

    const handleToggleExpand = (key) => {
        setExpandedTypes(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleRepositoryToggle = () => {
        setRepositories(prev => ({
            ...prev,
            enabled: !prev.enabled,
            connectionName: !prev.enabled ? prev.connectionName : '',
        }));
        if (errors.repositoryConnection) {
            setErrors(prev => { const next = { ...prev }; delete next.repositoryConnection; return next; });
        }
    };

    const handleRepositoryConnectionChange = (value) => {
        setRepositories(prev => ({ ...prev, connectionName: value }));
        if (errors.repositoryConnection) {
            setErrors(prev => { const next = { ...prev }; delete next.repositoryConnection; return next; });
        }
    };

    const clearScheduleErrors = (keys) => {
        setErrors(prev => {
            const next = { ...prev };
            keys.forEach(key => delete next[key]);
            return next;
        });
    };

    const clearNotificationErrors = () => {
        setErrors(prev => {
            const next = { ...prev };
            delete next.notificationsRequired;
            Object.values(NOTIFICATION_ERROR_KEYS).forEach(key => delete next[key]);
            return next;
        });
    };

    const applyScheduleToggle = () => {
        setSchedule(prev => (prev.enabled ? { ...initialSchedule } : { ...prev, enabled: true }));
        clearScheduleErrors(Object.values(SCHEDULE_ERROR_KEYS));
    };

    const handleScheduleToggle = () => {
        if (schedule.enabled && jobData?.status === "SCHEDULED") {
            setIsScheduleConfirmOpen(true);
        } else {
            applyScheduleToggle();
        }
    };

    const handleConfirmScheduleToggle = () => {
        applyScheduleToggle();
        setIsScheduleConfirmOpen(false);
    };

    const handleCancelScheduleToggle = () => {
        setIsScheduleConfirmOpen(false);
    };

    const handleScheduleChange = (field, value) => {
        setSchedule(prev => {
            const next = { ...prev, [field]: value };
            // Offset is only meaningful with retries — clear it when retries are removed
            if (field === 'retryAttempts' && !value) next.offsetTime = '';
            return next;
        });
        const errorKeys = [SCHEDULE_ERROR_KEYS[field]];
        if (field === 'retryAttempts' && !value) errorKeys.push(SCHEDULE_ERROR_KEYS.offsetTime);
        const keysToClear = errorKeys.filter((key) => key && errors[key]);
        if (keysToClear.length) clearScheduleErrors(keysToClear);
    };

    const handleScheduleModeChange = (mode) => {
        setSchedule(prev => ({ ...prev, mode }));
        clearScheduleErrors(['scheduleDate', 'scheduleTime', 'scheduleCron']);
    };

    const handleNotificationsToggle = () => {
        setNotifications(prev => (prev.enabled ? { ...initialNotifications } : { ...prev, enabled: true }));
        clearNotificationErrors();
    };

    // Returns true once the raw input passes format validation, so the child can
    // clear its field. Dedupe runs inside the state updater to use the latest list.
    // categoryKey selects which of the three independent recipient lists to update.
    const handleAddEmails = (categoryKey, rawInput) => {
        const candidates = (rawInput || '')
            .split(',')
            .map(v => v.trim())
            .filter(v => v !== '');
        if (candidates.length === 0) return false;

        const invalid = candidates.filter(v => !EMAIL_REGEX.test(v));
        if (invalid.length > 0) {
            toast.error(`Invalid email address: '${invalid.join("', '")}'`);
            return false;
        }

        // Duplicate checks are case-insensitive (Test@gmail.com === test@gmail.com),
        // while the originally-typed casing is what gets stored/displayed.
        const seenLower = new Set();
        const uniqueNew = candidates.filter(c => {
            const lower = c.toLowerCase();
            if (seenLower.has(lower)) return false;
            seenLower.add(lower);
            return true;
        });

        const errorKey = NOTIFICATION_ERROR_KEYS[categoryKey];

        // Only brand-new addresses count toward the cap; duplicates are ignored below.
        const existingLower = notifications[categoryKey].map(e => e.toLowerCase());
        const netNew = uniqueNew.filter(c => !existingLower.includes(c.toLowerCase()));
        if (notifications[categoryKey].length + netNew.length > MAX_NOTIFICATION_EMAILS) {
            setErrors(prev => ({
                ...prev,
                [errorKey]: `You can add a maximum of ${MAX_NOTIFICATION_EMAILS} Email IDs`,
            }));
            return false;
        }

        setNotifications(prev => {
            const prevLower = prev[categoryKey].map(e => e.toLowerCase());
            const dups = uniqueNew.filter(c => prevLower.includes(c.toLowerCase()));
            if (dups.length > 0) toast.error(`'${dups.join("', '")}' already exists`);
            const toAdd = uniqueNew.filter(c => !prevLower.includes(c.toLowerCase()));
            if (toAdd.length === 0) return prev;
            return { ...prev, [categoryKey]: [...toAdd.reverse(), ...prev[categoryKey]] };
        });

        if (errors[errorKey] || errors.notificationsRequired) {
            setErrors(prev => { const next = { ...prev }; delete next[errorKey]; delete next.notificationsRequired; return next; });
        }
        return true;
    };

    // Clears the category's email error once the user takes a corrective action
    // (removing a committed email, or editing the input after a rejected add).
    const clearEmailError = (categoryKey) => {
        const errorKey = NOTIFICATION_ERROR_KEYS[categoryKey];
        if (errors[errorKey] || errors.notificationsRequired) {
            setErrors(prev => { const next = { ...prev }; delete next[errorKey]; delete next.notificationsRequired; return next; });
        }
    };

    const handleRemoveEmail = (categoryKey, index) => {
        setNotifications(prev => ({ ...prev, [categoryKey]: prev[categoryKey].filter((_, i) => i !== index) }));
        clearEmailError(categoryKey);
    };

    // ─── Validation & Submit ──────────────────────────────────────────────────

    const validateForm = () => {
        const newErrors = {};
        const pairs = activePairsRef.current;

        if (!formData.sourceConnection) newErrors.sourceConnection = 'Source is required';
        if (!formData.targetConnection) newErrors.targetConnection = 'Target is required';

        if (formData.sourceConnection && formData.targetConnection &&
            formData.sourceConnection === formData.targetConnection) {
            if (errors.sourceConnection) newErrors.sourceConnection = errors.sourceConnection;
            else newErrors.targetConnection = errors.targetConnection;
        }

        if (connectionsReady) {
            pairs.forEach((pair, index) => {
                if (!pair.sourceSchema) newErrors[`sourceSchema_${index}`] = 'Source Schema is required';
                if (!pair.targetSchema) newErrors[`targetSchema_${index}`] = 'Target Schema is required';
                if (pair.addFilter) {
                    const hasAnyFilter =
                        pair.includePrefix.length > 0 || pair.includeSuffix.length > 0 ||
                        pair.excludePrefix.length > 0 || pair.excludeSuffix.length > 0;
                    if (!hasAnyFilter) {
                        newErrors[`filter_${index}`] = 'Please enter at least one Prefix or Suffix value in Include or Exclude';
                    }
                }
            });

            const { isAutomated, globalFilter } = schemaMetaRef.current;
            if (isAutomated && globalFilter?.enabled) {
                const hasAnyGlobalFilter =
                    globalFilter.includePrefix.length > 0 || globalFilter.includeSuffix.length > 0 ||
                    globalFilter.excludePrefix.length > 0 || globalFilter.excludeSuffix.length > 0;
                if (!hasAnyGlobalFilter) {
                    newErrors.globalFilter = 'Please enter at least one Prefix or Suffix value in Include or Exclude';
                }
            }

            const anySelected =
                objectTypeOptions.filter(opt => !opt.hasSubTypes && opt.key !== 'selectAll').some(opt => objectTypes[opt.key]) ||
                allSubTypes.some(sub => objectTypes[sub.key]);

            if (!anySelected) newErrors.objectTypes = 'Please select at least one object type';
        }

        if (repositories.enabled && !repositories.connectionName) {
            newErrors.repositoryConnection = 'Connection Name is required';
        }

        if (schedule.enabled) {
            if (schedule.mode === SCHEDULE_MODES.ONE_TIME) {
                if (!schedule.date) newErrors.scheduleDate = 'Date is required';
                if (!schedule.time) newErrors.scheduleTime = 'Time is required';
            } else {
                if (!schedule.cronExpression.trim()) {
                    newErrors.scheduleCron = 'CRON expression is required';
                } else {
                    const cron = validateCron(schedule.cronExpression);
                    if (!cron.valid) newErrors.scheduleCron = cron.error;
                }
            }

            if (schedule.retryAttempts && !schedule.offsetTime) {
                newErrors.scheduleOffset = 'Retry Interval is required';
            }
        }

        if (notifications.enabled) {
            const hasAnyEmail = notifications.failureEmails.length > 0 ||
                notifications.successEmails.length > 0 ||
                notifications.successWithChangeEmails.length > 0;
            if (!hasAnyEmail) {
                newErrors.notificationsRequired = 'At least one Email ID is required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e, runImmediately = false) => {
        e.preventDefault();
        if (!validateForm()) return;

        // A scheduled job runs at its scheduled time, never immediately.
        const runNow = runImmediately && !schedule.enabled;

        setIsLoading(true);
        try {
            const selectedObjectTypes = [
                ...allSubTypes
                    .filter(sub => objectTypes[sub.key])
                    .map(sub => sub.payloadKey ?? sub.key.replace(/([A-Z])/g, '_$1').toUpperCase()),
                ...objectTypeOptions
                    .filter(opt => !opt.hasSubTypes && opt.key !== 'selectAll' && objectTypes[opt.key])
                    .map(opt => opt.key.replace(/([A-Z])/g, '_$1').toUpperCase()),
            ];

            const { isAutomated, globalFilter } = schemaMetaRef.current;
            const gf = globalFilter;

            // Combine the one-time date + local wall-clock time into a single ISO instant.
            const scheduleRunAt = (schedule.mode === SCHEDULE_MODES.ONE_TIME && schedule.date && schedule.time)
                ? (() => {
                    const [y, mo, d] = schedule.date.split('-').map(Number);
                    const [h, mi] = schedule.time.split(':').map(Number);
                    return new Date(y, mo - 1, d, h, mi, 0, 0).toISOString();
                })()
                : null;

            const payload = {
                name: formData.jobName,
                source_connection_id: formData.sourceConnection,
                target_connection_id: formData.targetConnection,
                automated_comparison: isAutomated,
                global_include_prefix: (isAutomated && gf?.enabled && gf.includePrefix.length > 0) ? gf.includePrefix : null,
                global_include_suffix: (isAutomated && gf?.enabled && gf.includeSuffix.length > 0) ? gf.includeSuffix : null,
                global_exclude_prefix: (isAutomated && gf?.enabled && gf.excludePrefix.length > 0) ? gf.excludePrefix : null,
                global_exclude_suffix: (isAutomated && gf?.enabled && gf.excludeSuffix.length > 0) ? gf.excludeSuffix : null,
                schema_pairs: activePairsRef.current.map(pair => ({
                    source_schema: pair.sourceSchema,
                    target_schema: pair.targetSchema,
                    include_prefix: pair.addFilter ? pair.includePrefix : [],
                    include_suffix: pair.addFilter ? pair.includeSuffix : [],
                    exclude_prefix: pair.addFilter ? pair.excludePrefix : [],
                    exclude_suffix: pair.addFilter ? pair.excludeSuffix : [],
                })),
                object_types: selectedObjectTypes,
                github_connection_id: repositories.enabled ? repositories.connectionName : null,
                schedule: schedule.enabled ? {
                    is_enabled: true,
                    schedule_type: schedule.mode === SCHEDULE_MODES.CRON ? 'CRON' : 'ONE_TIME',
                    run_at: scheduleRunAt,
                    cron_expression: schedule.mode === SCHEDULE_MODES.CRON ? schedule.cronExpression.trim() : null,
                    retry_attempts: schedule.retryAttempts || null,
                    retry_offset_seconds: schedule.offsetTime || null,
                } : {
                    is_enabled: false,
                    schedule_type: 'ONE_TIME',
                    run_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
                    cron_expression: '',
                    retry_attempts: null,
                    retry_offset_seconds: null,
                },
                is_notification_enabled: notifications.enabled,
                notification_emails_failure: notifications.enabled ? notifications.failureEmails : [],
                notification_emails_success: notifications.enabled ? notifications.successEmails : [],
                notification_emails_success_with_changes: notifications.enabled ? notifications.successWithChangeEmails : [],
            };

            if (runNow) {
                jobService.updateJobAndRun(jobData.id, payload);
                await onSuccess?.();
                toast.success('Job updated and run successfully');
                handleModalClose();
            } else {
                const response = await jobService.updateJob(jobData.id, payload);
                if (response) {
                    await onSuccess?.();
                    toast.success('Job updated successfully');
                    handleModalClose();
                }
            }
        } catch (error) {
            toast.error(error.message || 'Failed to update job. Please try again.');
            console.error('Update job error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleModalClose = () => {
        setFormData(initialFormData);
        setObjectTypes(initialObjectTypes);
        setExpandedTypes(initialExpandedTypes);
        setSectionExpanded({ objectTypes: true, schemaComparisons: true });
        setRepositories(initialRepositories);
        setSchedule(initialSchedule);
        setNotifications(initialNotifications);
        setErrors({});
        setSchemas({ source: [], target: [] });
        setIsAutomatedJob(false);
        setInitialPairs(null);
        setInitialGlobalFilter(null);
        activePairsRef.current = [];
        onClose();
    };

    // ─── Derived data ─────────────────────────────────────────────────────────

    const connectionOptions = connections.map(conn => ({ value: conn.id, label: conn.name }));
    const schemasAvailable = schemas.source.length > 0 || schemas.target.length > 0;
    const repositoryConnectionOptions = repositoryConnections.map(conn => ({ value: conn.id, label: conn.name }));

    // ─── Render ───────────────────────────────────────────────────────────────

    const footer = (
        <>
            <button type="button" className="btn btn-cancel" onClick={handleModalClose}>
                Cancel
            </button>
            <button type="button" className="btn btn-secondary" onClick={(e) => handleSubmit(e, false)}>
                Update
            </button>
            <button
                type="submit"
                className="btn btn-primary"
                onClick={(e) => handleSubmit(e, true)}
                disabled={schedule.enabled}
                title={schedule.enabled ? 'Disabled while a schedule is set — the job will run at its scheduled time' : undefined}
            >
                Update & Run
            </button>
        </>
    );

    return (
        <>
            <BaseModal
                isOpen={isOpen}
                onClose={handleModalClose}
                title="Edit Job"
                footer={footer}
                className="add-job-modal"
                modalBodyRef={modalBodyRef}
            >
                <form onSubmit={(e) => handleSubmit(e, true)}>

                    {/* Section 1: Job Name + Source + Target */}
                    <div className="job-form-card">
                        <div className="form-row">
                            <div className="form-group form-group-full">
                                <label>Job Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Production vs UAT Comparison"
                                    value={formData.jobName}
                                    disabled
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Source <span className="required">*</span></label>
                                <Select
                                    value={connectionOptions.find(o => o.value === formData.sourceConnection) || null}
                                    onChange={(opt) => handleChange('sourceConnection', opt ? opt.value : '')}
                                    options={connectionOptions}
                                    placeholder={loadingConnections ? 'Loading connections...' : 'Select Connection'}
                                    isDisabled={loadingConnections}
                                    isLoading={loadingConnections}
                                    isSearchable
                                    isClearable
                                    styles={getSelectStyles(!!errors.sourceConnection)}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    closeMenuOnScroll={(e) => modalBodyRef?.current?.contains(e.target)}
                                    noOptionsMessage={() => 'No connections found'}
                                    components={{ DropdownIndicator, ClearIndicator }}
                                />
                                <ErrorMessage message={errors.sourceConnection} />
                            </div>
                            <div className="form-group">
                                <label>Target <span className="required">*</span></label>
                                <Select
                                    value={connectionOptions.find(o => o.value === formData.targetConnection) || null}
                                    onChange={(opt) => handleChange('targetConnection', opt ? opt.value : '')}
                                    options={connectionOptions}
                                    placeholder={loadingConnections ? 'Loading connections...' : 'Select Connection'}
                                    isDisabled={loadingConnections}
                                    isLoading={loadingConnections}
                                    isSearchable
                                    isClearable
                                    styles={getSelectStyles(!!errors.targetConnection)}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    closeMenuOnScroll={(e) => modalBodyRef?.current?.contains(e.target)}
                                    noOptionsMessage={() => 'No connections found'}
                                    components={{ DropdownIndicator, ClearIndicator }}
                                />
                                <ErrorMessage message={errors.targetConnection} />
                            </div>
                        </div>
                    </div>

                    {/* Section 2 & 3: visible once initialPairs are ready (schemas loaded) */}
                    {initialPairs !== null && (
                        <>
                            {/* Section 2: Object Types */}
                            <div className="job-form-card">
                                <button type="button" className="job-form-card-header">
                                    <span>Object Types to Compare <span className="required">*</span></span>
                                    <div
                                        className="job-form-card-chevron-wrapper"
                                        onClick={() => setSectionExpanded(prev => ({ ...prev, objectTypes: !prev.objectTypes }))}
                                    >
                                        <ChevronDown className={`job-form-card-chevron ${sectionExpanded.objectTypes ? 'expanded' : ''}`} />
                                    </div>
                                </button>
                                {sectionExpanded.objectTypes && (
                                    <div className="job-form-card-body">
                                        <ObjectTypeSelector
                                            objectTypeOptions={objectTypeOptions}
                                            subTypesMap={subTypesMap}
                                            objectTypes={objectTypes}
                                            expandedTypes={expandedTypes}
                                            disabled={loadingSchemas || !schemasAvailable}
                                            error={errors.objectTypes}
                                            onTypeChange={handleObjectTypeChange}
                                            onToggleExpand={handleToggleExpand}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Section 3: Schema Comparisons */}
                            <div className="job-form-card">
                                <button type="button" className="job-form-card-header">
                                    <span>Schema Comparisons</span>
                                    <div
                                        className="job-form-card-chevron-wrapper"
                                        onClick={() => setSectionExpanded(prev => ({ ...prev, schemaComparisons: !prev.schemaComparisons }))}
                                    >
                                        <ChevronDown className={`job-form-card-chevron ${sectionExpanded.schemaComparisons ? 'expanded' : ''}`} />
                                    </div>
                                </button>
                                {sectionExpanded.schemaComparisons && (
                                    <div className="job-form-card-body">
                                        <SchemaComparisons
                                            schemas={schemas}
                                            loadingSchemas={loadingSchemas}
                                            schemasAvailable={schemasAvailable}
                                            connectionsReady={connectionsReady}
                                            errors={errors}
                                            setErrors={setErrors}
                                            onPairsChange={handlePairsChange}
                                            initialTab={isAutomatedJob ? 'automated' : 'manual'}
                                            initialPairs={initialPairs}
                                            initialGlobalFilter={initialGlobalFilter}
                                            modalBodyRef={modalBodyRef}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Section 4: Repositories */}
                            <div className="job-form-card">
                                <div className="job-form-card-header">
                                    <span>Repositories</span>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            className="toggle-switch-input"
                                            checked={repositories.enabled}
                                            onChange={handleRepositoryToggle}
                                            disabled={!schemasAvailable}
                                        />
                                        <span className="toggle-switch-slider" />
                                    </label>
                                </div>
                                {repositories.enabled && (
                                    <div className="job-form-card-body repositories-body">
                                        <div className="form-group form-group-full">
                                            <label>
                                                Select Connection Name <span className="required">*</span>
                                            </label>
                                            <div className="repositories-connection-row">
                                                <div ref={repoSelectRef}>
                                                    <Select
                                                        value={repositoryConnectionOptions.find(o => o.value === repositories.connectionName) || null}
                                                        onChange={(opt) => handleRepositoryConnectionChange(opt ? opt.value : '')}
                                                        options={repositoryConnectionOptions}
                                                        placeholder="Select Connection"
                                                        isDisabled={!schemasAvailable}
                                                        isSearchable
                                                        isClearable
                                                        styles={getSelectStyles(!!errors.repositoryConnection)}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        menuPlacement={repoPlacement}
                                                        onMenuOpen={() => setRepoPlacement(calculatePlacement(repoSelectRef, repositoryConnectionOptions.length))}
                                                        closeMenuOnScroll={(e) => modalBodyRef?.current?.contains(e.target)}
                                                        noOptionsMessage={() => 'No connections found'}
                                                        components={{ DropdownIndicator, ClearIndicator }}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn-add-new-connection"
                                                    onClick={() => setIsAddRepositoryOpen(true)}
                                                    disabled={!schemasAvailable}
                                                >
                                                    + Add New
                                                </button>
                                            </div>
                                            <ErrorMessage message={errors.repositoryConnection} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Section 5: Notifications */}
                            <div className="job-form-card">
                                <div className="job-form-card-header">
                                    <span>Notifications</span>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            className="toggle-switch-input"
                                            checked={notifications.enabled}
                                            onChange={handleNotificationsToggle}
                                        />
                                        <span className="toggle-switch-slider" />
                                    </label>
                                </div>
                                {notifications.enabled && (
                                    <div className="job-form-card-body">
                                        <NotificationsSection
                                            notifications={notifications}
                                            errors={errors}
                                            onAddEmails={handleAddEmails}
                                            onRemoveEmail={handleRemoveEmail}
                                            onEmailInputChange={clearEmailError}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Section 6: Schedule Job Run */}
                            <div className="job-form-card">
                                <div className="job-form-card-header">
                                    <span>Schedule Job Run</span>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            className="toggle-switch-input"
                                            checked={schedule.enabled}
                                            onChange={handleScheduleToggle}
                                            disabled={!schemasAvailable}
                                        />
                                        <span className="toggle-switch-slider" />
                                    </label>
                                </div>
                                {schedule.enabled && (
                                    <div className="job-form-card-body">
                                        <ScheduleJobRun
                                            schedule={schedule}
                                            errors={errors}
                                            onChange={handleScheduleChange}
                                            onModeChange={handleScheduleModeChange}
                                            modalBodyRef={modalBodyRef}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                </form>
            </BaseModal>

            {isAddRepositoryOpen && (
                <AddRepositoryModal
                    isOpen={isAddRepositoryOpen}
                    onClose={() => setIsAddRepositoryOpen(false)}
                    onSuccess={async () => {
                        setIsAddRepositoryOpen(false);
                        await fetchRepositoryConnections();
                    }}
                    setIsLoading={setIsLoading}
                />)
            }

            <ConfirmationModal
                isOpen={isScheduleConfirmOpen}
                onClose={handleCancelScheduleToggle}
                onConfirm={handleConfirmScheduleToggle}
                title="Confirm Action"
                message="Do you want to proceed?"
                confirmText="Confirm"
                cancelText="Cancel"
            />
        </>
    );
};

export default EditJobModal;